import { Color } from 'libs/end-to-end/src/types/branding/color';
import { z } from 'zod';

export const S2GroupedBarChartKey = z.union([z.string(), z.number()]);

export type S2GroupedBarChartKey = z.infer<typeof S2GroupedBarChartKey>;

export const S2GroupedBarChartGroupLevelSerializable = z.object({
  id: S2GroupedBarChartKey,
  value: z.number(),
});

export type S2GroupedBarChartGroupLevelSerializable = z.infer<
  typeof S2GroupedBarChartGroupLevelSerializable
>;

export const S2GroupedBarChartGroupSerializable = z.object({
  id: S2GroupedBarChartKey,
  label: z.string(),
  groupLevels: S2GroupedBarChartGroupLevelSerializable.array(),
});

export type S2GroupedBarChartGroupSerializable = z.infer<
  typeof S2GroupedBarChartGroupSerializable
>;

export const S2GroupedBarChartLevel = z.object({
  id: S2GroupedBarChartKey,
  label: z.string(),
  color: Color.optional(),
});

export type S2GroupedBarChartLevel = z.infer<typeof S2GroupedBarChartLevel>;

export const S2GroupedBarChartUnit = z.union([
  z.literal('percentage'),
  z.literal('count'),
  z.literal('dollars'),
]);

export type S2GroupedBarChartUnit = z.infer<typeof S2GroupedBarChartUnit>;

export const S2GroupedBarChartSerializableProps = z.object({
  xGroups: S2GroupedBarChartGroupSerializable.array(),
  xLevels: S2GroupedBarChartLevel.array(),
  yAxisLabel: z.string(),
  xAxisLabel: z.string().nullable().optional(),
  units: S2GroupedBarChartUnit,

  /**
   * Whether the height is fixed or not.
   * This will default to false if not provided.
   * When false, responsive height (see heightAsFactorOfWidth) will be used instead.
   */
  horizontalFixedHeight: z.boolean().optional(),

  /**
   * The minimum height of the line chart, in pixels.
   */
  horizontalMinHeight: z.number().optional(),

  /**
   * The maximum height of the line chart, in pixels.
   */
  horizontalMaxHeight: z.number().optional(),

  /**
   * The height of the line chart as a factor of the width.
   * This property is only used when `fixedHeight` is false.
   * If you set this to 2, the height will be twice the width.
   * If you set this to 0.5, the height will be half the width.
   * This will default to 0.565 if not provided.
   */
  horizontalHeightAsFactorOfWidth: z.number().optional(),

  /**
   * The height of the line chart, in pixels.
   * This property is only used when `fixedHeight` is true.
   */
  horizontalHeight: z.number().optional(),

  /**
   * The minimum width of the bars, in pixels, before displaying the bars vertically.
   */
  horizontalMinBarWidth: z.number().optional(),

  /**
   * The padding between groups of bars, in pixels.
   */
  horizontalGroupPadding: z.number().optional(),

  /**
   * Whether the vertical bars should have a fixed thickness when displayed vertically.
   * The default is false.
   */
  verticalBarFixedThickness: z.boolean().optional(),

  /**
   * The thickness of the bars when displayed vertically.
   * The default for this is 20.
   * This property is only used when `verticalBarFixedThickness` is true.
   */
  verticalBarThickness: z.number().optional(),

  /**
   * The thickness of the bars when displayed vertically as a factor of the width.
   * The default for this is 0.02.
   * This property is only used when `verticalBarFixedThickness` is false.
   */
  verticalBarThicknessAsFactorOfWidth: z.number().optional(),

  /**
   * The minimum thickness (in pixels) of the bars when displayed vertically.
   * The default for this is 8.
   * This property is only used when `verticalBarFixedThickness` is false.
   */
  verticalBarMinThickness: z.number().optional(),

  /**
   * The maximum thickness (in pixels) of the bars when displayed vertically.
   * The default for this is 50.
   */
  verticalBarMaxThickness: z.number().optional(),

  /**
   * Vertical min height for the chart when verticalBarThicknessAsFactorOfWidth is applied
   */
  verticalMinHeight: z.number().optional(),

  /**
   * Vertical max height for the chart when verticalBarThicknessAsFactorOfWidth is applied
   */
  verticalMaxHeight: z.number().optional(),

  // Padding to be applied before, after and between groups of bars. Value is
  // calculated as a factor of height
  verticalGroupPaddingAsFactorOfHeight: z.number().optional(),

  // Padding to be applied between bars. Value must be in px.
  verticalInnerPadding: z.number().optional(),

  margin: z
    .object({
      left: z.number(),
      top: z.number(),
      right: z.number(),
      bottom: z.number(),
    })
    .optional(),

  // layers? : BarLayer < BarDatum > [];

  hoverEffect: z.boolean().optional(),

  /**
   * Whether to display the grid lines on the x-axis.
   */
  enableGridX: z.boolean().optional(),

  /**
   * Whether to display the grid lines on the y-axis.
   */
  enableGridY: z.boolean().optional(),

  /**
   * The fills to be applied to the bars.
   */
  fills: z
    .array(
      z.object({
        match: z.object({
          id: z.string(),
        }),
        id: z.string(),
      }),
    )
    .optional(),

  /**
   * The defs to be applied to the bars.
   */
  defs: z
    .array(
      z.object({
        id: z.string(),
        type: z.string(),
      }),
    )
    .optional(),

  /**
   * The markers to be applied to the bars.
   */
  markers: z
    .array(
      z.object({
        axis: z.literal('x').or(z.literal('y')),
        value: z.number(),
        legend: z.string().optional(),
        legendOrientation: z
          .literal('horizontal')
          .or(z.literal('vertical'))
          .optional(),
        legendPosition: z.enum(['top', 'right', 'bottom', 'left']).optional(),
        lineStyle: z.object({
          stroke: z.string(),
          strokeWidth: z.string(),
        }),
      }),
    )
    .optional(),

  /**
   * Whether to display the axis.
   */
  enableAxisX: z.boolean().optional(),
  enableAxisY: z.boolean().optional(),

  /**
   * The theme to be applied to the chart. This is partially defined and should be updated
   * as we add more properties.
   */
  theme: z
    .object({
      axis: z.object({
        domain: z.object({
          line: z.object({
            stroke: z.string(),
            strokeWidth: z.string(),
          }),
        }),
      }),
    })
    .optional(),

  maxYAxisLabelWidthPx: z.number().optional(),
});

export type S2GroupedBarChartSerializableProps = z.infer<
  typeof S2GroupedBarChartSerializableProps
>;
